import { useMemo } from 'react';
import { Capability } from 'homey-lib/webpack/index.js';

import { DeviceTile } from './DeviceTile';

import { useCapabilityValues } from '../../store/devices/useCapabilityValues';

import { IndicatorLight } from './IndicatorLight';
import { IndicatorLock } from './IndicatorLock';
import { IndicatorSpeaker } from './IndicatorSpeaker';
import { IndicatorThermostat } from './IndicatorThermostat';
import { IndicatorAlarm } from './IndicatorAlarm';
import { IndicatorAlarmSpecific } from './IndicatorAlarmSpecific';
import { IndicatorNumber } from './IndicatorNumber';
import { IndicatorBattery } from './IndicatorBattery';
import { IndicatorString } from './IndicatorString';

export const dangerCapabilityIdList = ['alarm_co', 'alarm_fire', 'alarm_smoke'];

export const defaultCapabilityIdOrderList = [
  'alarm_motion',
  'alarm_contact',
  'alarm_co2',
  'alarm_pm25',
  'alarm_heat',
  'alarm_water',
  'measure_temperature',
  'measure_co',
  'measure_co2',
  'measure_pm25',
  'alarm_generic',
  'alarm_tamper',
  'alarm_night',
  'measure_power',
  'measure_humidity',
  'measure_pressure',
  'meter_power',
  'meter_water',
  'meter_gas',
  'meter_rain',
  'measure_voltage',
  'measure_current',
  'measure_luminance',
  'measure_ultraviolet',
  'measure_water',
  'measure_noise',
  'measure_rain',
  'measure_wind_strength',
  'measure_wind_angle',
  'measure_gust_strength',
  'measure_gust_angle',
  'measure_battery',
  'alarm_battery',
];

export function IndicatorCapability({ device, tileSize }) {
  const deviceClass = getIndicatorDeviceClass(device);
  const uiIndicator = device.uiIndicator;

  const { isDangerAlarm } = useDangerAlarm({
    deviceId: device.id,
    capabilitiesObj: device.capabilitiesObj,
  });

  const { Component, capabilityId } = useMemo(() => {
    if (device.capabilitiesObj == null) {
      return {};
    }

    const capabilityList = Object.values(device.capabilitiesObj);
    const capabilityId = uiIndicator ?? getDefaultCapabilityId(capabilityList);

    if (capabilityId === '.none') {
      return {};
    }

    const returnValue = {
      Component: null,
      capabilityId: capabilityId,
    };

    switch (true) {
      case deviceClass === 'light':
        returnValue.Component = IndicatorLight;
        break;
      case deviceClass === 'lock':
        returnValue.Component = IndicatorLock;
        break;
      case deviceClass === 'speaker':
        returnValue.Component = IndicatorSpeaker;
        break;
      case deviceClass === 'thermostat':
        returnValue.Component = IndicatorThermostat;
        break;
      case capabilityId != null && device.capabilitiesObj[capabilityId]?.type === 'string':
        returnValue.Component = IndicatorString;
        break;
      case capabilityId != null && isBatteryIndicator(capabilityId):
        returnValue.Component = IndicatorBattery;
        break;
      case capabilityId != null &&
        isNumberIndicator(capabilityId) &&
        tileSize === DeviceTile.size.medium:
        returnValue.Component = IndicatorNumber;
        break;
      case uiIndicator != null && isSpecificAlarmIndicator(uiIndicator):
        returnValue.Component = IndicatorAlarmSpecific;
        break;
      case deviceClass === 'sensor':
      case capabilityList.some((capability) => {
        return capability.id.startsWith('alarm_');
      }):
        returnValue.Component = IndicatorAlarm;
        break;
      default:
        break;
    }

    return returnValue;
  }, [device.capabilitiesObj, deviceClass, uiIndicator, tileSize]);

  if (isDangerAlarm === true) {
    return <IndicatorAlarm device={device} capabilities={device.capabilitiesObj} />;
  }

  if (Component == null) {
    return null;
  }

  return <Component device={device} capabilityId={capabilityId} />;
}

function isNumberIndicator(capabilityId) {
  return (
    capabilityId.startsWith('meter_') ||
    capabilityId.startsWith('measure_') ||
    capabilityId.startsWith('target_')
  );
}

function isSpecificAlarmIndicator(capabilityId) {
  return capabilityId.startsWith('alarm_');
}

function isBatteryIndicator(capabilityId) {
  return (
    Capability.isInstanceOfId(capabilityId, 'alarm_battery') ||
    Capability.isInstanceOfId(capabilityId, 'measure_battery')
  );
}

function getIndicatorDeviceClass(device) {
  switch (device.virtualClass) {
    case 'light':
      return device.virtualClass;
    default:
      return device.class;
  }
}

function getDefaultCapabilityId(capabilityList) {
  let higestPrecedenceCapabilityId = null;
  let prevIndex = Infinity;

  for (const capability of capabilityList) {
    const [baseId] = capability.id.split('.');
    const index = defaultCapabilityIdOrderList.indexOf(baseId);

    if (index === -1) continue;

    // Higher precedence.
    if (index < prevIndex) {
      prevIndex = index;
      higestPrecedenceCapabilityId = capability.id;
      // Continue here because we are overwriting the prevIndex.
      continue;
    }

    // Same precedence.
    if (index === prevIndex) {
      // This means the base id should be used. Because we have encountered for example
      // measure_power and measure_power.foo or measure_power.foo and measure_power. We
      // need this case because devices can also only have measure_power.foo and not the base
      // capability.
      if (higestPrecedenceCapabilityId !== baseId) {
        higestPrecedenceCapabilityId = baseId;
      }
    }
  }

  return higestPrecedenceCapabilityId;
}

function useDangerAlarm({ deviceId, capabilitiesObj }) {
  const dangerCapabilityIds = useMemo(() => {
    if (capabilitiesObj == null) {
      return [];
    }

    const result = [];

    for (const capabilityObj of Object.values(capabilitiesObj)) {
      const baseId = capabilityObj.id.split('.')[0];

      if (dangerCapabilityIdList.includes(baseId)) {
        result.push(capabilityObj.id);
      }
    }

    return result;
  }, [capabilitiesObj]);

  const [values] = useCapabilityValues({
    deviceId: deviceId,
    capabilityIds: dangerCapabilityIds,
  });

  const isDangerAlarm = useMemo(() => {
    return Object.values(values).some((value) => value === true);
  }, [values]);

  return { isDangerAlarm };
}
